import { Icon, Table } from "semantic-ui-react";
import cn from "classnames";

import { type ColumnOption } from "../../../../../interfaces/columnOptions";
import { SurveyQuestionType, type SurveyQuestion, type SurveyQuestionAnswer } from "../../types/state";

import { TextTruncate } from "../../../../../components";
import { PerformanceTabSectionList } from "features/Library/Common/Performance/PerformanceTabSectionList/PerformanceTabSectionList";
import { ListViewBase } from "../../../../../components/listView";

import "./SurveyPerformanceList.scss";
import { NoFilteredData } from "../../../../../components/charts";
import { completes } from "features/Library/Common/utils/performanceUtils";
import { roundToTwoDigits } from "features/Library/Common/utils/roundToTwoDigits";
import { Tooltip } from "components/common/tooltip";
import { PerformanceLinkButton } from "features/Library/Common/Buttons/PerformanceLinkButton/PerformanceLinkButton";
import { useCallback, useMemo, type FC } from "react";
import { scaleLinear } from "d3";
import { useDimensions } from "hooks/useDimensions";

const getQuestionType = (type: SurveyQuestionType): string =>
  ({
    [SurveyQuestionType.MultipleSelect]: "Multiple Select",
    [SurveyQuestionType.SingleSelect]: "Single Select",
    [SurveyQuestionType.FreeText]: "Free Text",
    [SurveyQuestionType.RatingScale]: "Rating Scale",
  })[type];

interface Props {
  questions: SurveyQuestion[];
  selectQuestion: (question: SurveyQuestion) => void;
  selectAnswer: (answer: SurveyQuestionAnswer) => void;
  reportEnabled: boolean;
}

export const SurveyPerformanceList = ({
  questions,
  selectQuestion,
  selectAnswer,
  reportEnabled,
}: Props): React.ReactElement => {
  const getQuestionIndex = useCallback((question: SurveyQuestion): number => questions.indexOf(question), [questions]);

  const renderQuestionDrillDownLink = useCallback(
    (question: SurveyQuestion): React.ReactElement => (
      <span className="shortened-survey-question">
        <PerformanceLinkButton onClick={() => selectQuestion(question)}>
          <Tooltip
            target={<TextTruncate className="survey-question">{question.QuestionText}</TextTruncate>}
            content={question.QuestionText}
          />
        </PerformanceLinkButton>
      </span>
    ),
    [selectQuestion],
  );

  const columns: ColumnOption<SurveyQuestion>[] = useMemo(
    () => [
      {
        name: "Question",
        width: 7,
        isSortable: false,
        render(question) {
          return (
            <div className="survey-question-box">
              <div className="survey-question-number">{`${getQuestionIndex(question) + 1}. `}</div>
              <div className="survey-question-answer-container">
                {renderQuestionDrillDownLink(question)}
                <Answers
                  question={question}
                  selectQuestion={selectQuestion}
                  selectAnswer={selectAnswer}
                  reportEnabled={reportEnabled}
                />
              </div>
            </div>
          );
        },
      },
      {
        name: "Type",
        width: 3,
        isSortable: false,
        render(question) {
          return getQuestionType(question.QuestionType);
        },
      },
      {
        name: completes,
        width: 3,
        isSortable: false,
        render(question) {
          return question.Completions?.toLocaleString() ?? 0;
        },
      },
      {
        name: "Skipped",
        width: 3,
        isSortable: false,
        render(question) {
          return question.Skipped?.toLocaleString() ?? 0;
        },
      },
      {
        name: "Completion Rate",
        width: 3,
        isSortable: false,
        render(question) {
          return reportEnabled
            ? `${roundToTwoDigits((question.CompletionRate ?? 0) * 100)}%`
            : `${question.CompletionRate || 0}%`;
        },
      },
    ],
    [getQuestionIndex, renderQuestionDrillDownLink, reportEnabled, selectAnswer, selectQuestion],
  );

  const buildTableBody = useCallback(
    (item: SurveyQuestion) => {
      return columns.map((column, index) => (
        <Table.Cell
          className={cn("table-body-cell", { "table-body-cell--required": item.IsQuestionRequired && index === 0 })}
          width={column.width}
          key={`${column.name}_${index}`}
        >
          {column.render(item)}
        </Table.Cell>
      ));
    },
    [columns],
  );

  return (
    <PerformanceTabSectionList
      title="Questions & Answers"
      count={questions.length}
      renderList={(page) => {
        return questions.length ? (
          <ListViewBase
            columnOptions={columns}
            items={questions}
            itemsAmount={questions.length}
            buildTableBody={buildTableBody}
            withFooter={false}
            activePage={page}
            withSelection={false}
          />
        ) : (
          <NoFilteredData />
        );
      }}
    />
  );
};

interface AnswerProp {
  question: SurveyQuestion;
  selectQuestion: (question: SurveyQuestion) => void;
  selectAnswer: (answer: SurveyQuestionAnswer) => void;
  reportEnabled: boolean;
}

const Answers: FC<AnswerProp> = ({ question, selectQuestion, selectAnswer, reportEnabled }) => {
  // Measure the width of the gap for the bars in case of rating scale
  const [dimensions, ref] = useDimensions();
  const xScale = useMemo(() => {
    const answersMax =
      question.QuestionType === SurveyQuestionType.RatingScale
        ? Math.max(...question.Answers.map((a) => a.AnsweredPeople), 1)
        : 0;
    return scaleLinear()
      .domain([0, answersMax])
      .range([0, dimensions?.width ?? 1]);
  }, [question.Answers, question.QuestionType, dimensions]);
  return (
    <ul className="survey-answers">
      {question.QuestionType === SurveyQuestionType.RatingScale && (
        <div className="survey-answers__rating-scale">
          <Tooltip
            target={<TextTruncate>{question.HighestRankLabel}</TextTruncate>}
            content={question.HighestRankLabel}
          />
        </div>
      )}
      {question.Answers.map((answer, i) => {
        const formattedAnswers =
          question.QuestionType === SurveyQuestionType.FreeText ? "View Answers" : answer.AnswerText;

        return (
          <li className="survey-answers__answer" key={answer.AnswerId}>
            <PerformanceLinkButton
              className={cn({
                "survey-answers__answer-button": question.QuestionType === SurveyQuestionType.RatingScale,
              })}
              onClick={() => {
                selectQuestion(question);
                selectAnswer(answer);
              }}
            >
              <Tooltip target={<TextTruncate>{formattedAnswers}</TextTruncate>} content={formattedAnswers} />
            </PerformanceLinkButton>
            {question.QuestionType === SurveyQuestionType.RatingScale && (
              <div ref={i === 0 ? ref : null} className="bar-container">
                <div className="bar" style={{ width: `${xScale(answer.AnsweredPeople)}px` }} />
              </div>
            )}
            <span className="survey-answer-percentage">
              <Icon className="fas fa-comment outline" />
              {`${answer.AnsweredPeople.toLocaleString() ?? 0}${!reportEnabled ? "%" : ""}`}
            </span>
          </li>
        );
      })}
      {question.QuestionType === SurveyQuestionType.RatingScale && (
        <div className="survey-answers__rating-scale">
          <Tooltip
            target={<TextTruncate>{question.LowestRankLabel}</TextTruncate>}
            content={question.LowestRankLabel}
          />
        </div>
      )}
    </ul>
  );
};
